import type { Address, Hex } from "viem";
import type { PrivateKeyAccount } from "viem/accounts";
import { ApiError, H, decodeReceiptHeader, paidHeaders, type ApiReceipt, type Receipt } from "@woodpecker/protocol";
import type { ServiceEntry } from "./chain";
import { HttpError, readBody, type MemoryClient, type StoreClient, type StoreFileView } from "./clients";
import { paidWrite, type PendingAnswer } from "./paid-write";

/** The receipt of a paid write as the extension API carries it (D76). */
export function apiReceiptOf(r: Receipt, tx: Hex, symbol: string): ApiReceipt {
  return { token: symbol, actual: r.actual, tx };
}

/** A failed settle (402 settle_failed with its reason) as the API's error codes say it (D76). */
export function apiErrorOf(e: unknown): ApiError {
  if (e instanceof ApiError) return e;
  if (e instanceof HttpError) {
    const reason = String(e.body.reason ?? "");
    if (e.status === 402 && reason === "cap")
      return new ApiError("cap_exceeded", `the ${String(e.body.token ?? "")} cap of this extension is used up`);
    if (e.status === 402 && reason === "balance")
      return new ApiError("balance", `the wallet holds too few ${String(e.body.token ?? "")}`);
    return new ApiError("failed", `${e.code}${e.body.detail ? `: ${String(e.body.detail)}` : ""}`);
  }
  return new ApiError("failed", e instanceof Error ? e.message : String(e));
}

/**
 * config.put (D74, D76): a commit to the extension's namespace in the memory service, paid by the extension's own
 * identity: pending with the memory's receipt, the Intent signed in the browser with that identity, settled by the
 * memory's submitter, and only then written. The same path as the settings record's writes (paidWrite).
 */
export async function paidExtConfigWrite(opts: {
  memory: MemoryClient;
  entry: ServiceEntry;
  wallet: Address;
  payer: PrivateKeyAccount;
  chainId: number;
  contractA: Address;
  id: string;
  config: Record<string, unknown>;
}): Promise<{ tx: Hex; config: Record<string, unknown>; receipt: ApiReceipt }> {
  const { result, receipt } = await paidWrite({
    entry: opts.entry,
    wallet: opts.wallet,
    payer: opts.payer,
    chainId: opts.chainId,
    contractA: opts.contractA,
    pending: () => opts.memory.putExtConfigPending(opts.id, opts.config, { payer: opts.payer.address }),
    settle: (calls, signed) => opts.memory.settleExtConfig(opts.id, calls, signed),
  });
  return { tx: result.tx, config: result.config, receipt: apiReceiptOf(receipt, result.tx, opts.entry.token) };
}

/**
 * store.put (D74, D76): a put to the store chosen at install, paid by the extension's identity per MiB started:
 * pending with the store's receipt, the Intent signed in the browser with that identity, settled by the store's
 * submitter, and only then kept.
 */
export async function paidStorePut(opts: {
  store: StoreClient;
  entry: ServiceEntry;
  wallet: Address;
  payer: PrivateKeyAccount;
  chainId: number;
  contractA: Address;
  id: string;
  name: string;
  bytes: Uint8Array;
  type: string;
}): Promise<{ tx: Hex; file: StoreFileView; receipt: ApiReceipt }> {
  const { result, receipt } = await paidWrite({
    entry: opts.entry,
    wallet: opts.wallet,
    payer: opts.payer,
    chainId: opts.chainId,
    contractA: opts.contractA,
    pending: () => opts.store.putPending(opts.id, opts.name, opts.bytes, { payer: opts.payer, type: opts.type }),
    settle: (calls, signed) => opts.store.settlePut(opts.id, opts.name, calls, signed),
  });
  return { tx: result.tx, file: result.file, receipt: apiReceiptOf(receipt, result.tx, opts.entry.token) };
}

/**
 * services.call (D76): one request to a service of the manifest, with the four headers of the extension's identity.
 * A free answer (200) comes back as it is. A paid write answers 202 pending with its receipt: the Console checks it,
 * signs the Intent with the extension's identity and settles at `<route>/settle`, as the memory's and the host's
 * paid writes do (CONTRACT.md 6.1, 8.1); the service's answer to the settle is the result.
 */
export async function paidServiceCall(opts: {
  entry: ServiceEntry;
  wallet: Address;
  payer: PrivateKeyAccount;
  chainId: number;
  contractA: Address;
  route: string;
  body: unknown;
}): Promise<{ result: unknown; receipt: ApiReceipt | null }> {
  const { entry } = opts;
  const text = opts.body === undefined ? "" : typeof opts.body === "string" ? opts.body : JSON.stringify(opts.body);
  const call = `0x${crypto.getRandomValues(new Uint8Array(16)).reduce((s, b) => s + b.toString(16).padStart(2, "0"), "")}`;
  const headers = await paidHeaders(opts.payer, opts.wallet, entry.name, call, text, { close: true });
  const res = await fetch(`${entry.origin}${opts.route}`, { method: "POST", headers, body: text });
  const data = await readBody(res);
  if (res.status === 202 && data.receipt) {
    const pending = data as unknown as PendingAnswer;
    const { result, receipt } = await paidWrite<{ body: Record<string, unknown>; tx: Hex }>({
      entry,
      wallet: opts.wallet,
      payer: opts.payer,
      chainId: opts.chainId,
      contractA: opts.contractA,
      pending: async () => ({ ...pending, call: pending.call ?? call }),
      settle: async (calls, signed) => {
        // the settle names the identity too: a gateway finds the call's receipt by (client, identity)
        const settleBody = JSON.stringify({ ...signed, calls });
        const settleHeaders = await paidHeaders(opts.payer, opts.wallet, entry.name, `${call}:settle`, settleBody, {
          close: true,
        });
        const s = await fetch(`${entry.origin}${opts.route}/settle`, {
          method: "POST",
          headers: settleHeaders,
          body: settleBody,
        });
        const d = await readBody(s);
        if (!s.ok) throw new HttpError(s.status, String(d.error ?? s.status), d);
        // a memory or a store answers { tx }; a gateway delivers the result itself, its tx in the receipt header
        const tx = (
          typeof d.tx === "string" ? d.tx : (decodeReceiptHeader(s.headers.get(H.receipt))?.tx ?? "0x")
        ) as Hex;
        return { body: d, tx };
      },
    });
    return { result: result.body, receipt: apiReceiptOf(receipt, result.tx, entry.token) };
  }
  if (!res.ok) throw new HttpError(res.status, String(data.error ?? res.status), data);
  return { result: data, receipt: null };
}
